/**
 * pdf.js layer for the web, with process-wide caches.
 *
 * PDFs are big — a 20 MB file rendered page-by-page — so the whole app shares
 * one parsed document per blob CID and one small LRU of rendered pages. A
 * carousel scrolling past a post does not re-download or re-parse anything,
 * and swiping between pages is a canvas blit instead of a render.
 */
import * as pdfjsLib from 'pdfjs-dist'

import {logger} from '#/logger'

/** Served from `public/static/js`, see `scripts/copy-pdf-worker.mjs`. */
export const PDF_WORKER_SRC = '/static/js/pdf.worker.min.mjs'
export const PDF_STANDARD_FONTS_URL = '/static/js/pdf-standard-fonts/'

let configured = false

function configurePdfjs() {
  if (configured) return
  configured = true
  pdfjsLib.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC
}

/**
 * Counts the pages of a PDF from its bytes without caching the parsed
 * document. pdf.js hands the buffer it is given to the worker (detaching it),
 * so we always pass a copy — the composer still needs its own bytes to upload.
 *
 * This is the only entry point that may be called before any carousel has
 * mounted, so it must configure pdf.js itself.
 */
export async function countPdfPages(bytes: Uint8Array): Promise<number> {
  configurePdfjs()
  const task = pdfjsLib.getDocument({
    data: bytes.slice(),
    standardFontDataUrl: PDF_STANDARD_FONTS_URL,
  })
  try {
    const doc = await task.promise
    return doc.numPages
  } finally {
    await task.destroy()
  }
}

export type PdfSource =
  | {
      kind: 'remote'
      /** Cache key — usually the blob CID */
      key: string
      url: string
      headers?: Record<string, string>
    }
  | {
      kind: 'bytes'
      key: string
      /** Local bytes, eg. a freshly picked file in the composer */
      bytes: Uint8Array
    }

export type RenderedPdfPage = {
  canvas: HTMLCanvasElement
  /** Size the page was rendered for, in CSS pixels */
  width: number
  height: number
  /** width / height */
  ratio: number
}

type DocEntry = {
  promise: Promise<pdfjsLib.PDFDocumentProxy>
  doc?: pdfjsLib.PDFDocumentProxy
  leases: number
  lastUsedAt: number
}

const docs = new Map<string, DocEntry>()
const MAX_LIVE_DOCUMENTS = 2

/** Pages rendered at carousel/fullscreen sizes */
const largePages = new Map<string, RenderedPdfPage>()
/** Pages rendered at thumbnail sizes */
const smallPages = new Map<string, RenderedPdfPage>()
const MAX_LARGE_PAGES = 6
const MAX_SMALL_PAGES = 24
const pendingPages = new Map<string, Promise<RenderedPdfPage>>()

const THUMBNAIL_BUCKET_MAX = 240
const BUCKET_SIZE = 120
/** Keep both dimensions under Safari's 4096px canvas limit */
const MAX_CANVAS_DIMENSION = 4096

function releaseCanvas(page: RenderedPdfPage) {
  // Zeroing the dimensions is the only reliable way to tell the browser it can
  // reclaim the backing store.
  page.canvas.width = 0
  page.canvas.height = 0
}

function bucketFor(targetWidth: number) {
  return Math.max(
    BUCKET_SIZE,
    Math.round(targetWidth / BUCKET_SIZE) * BUCKET_SIZE,
  )
}

function pickCache(bucket: number) {
  return bucket <= THUMBNAIL_BUCKET_MAX
    ? {cache: smallPages, max: MAX_SMALL_PAGES}
    : {cache: largePages, max: MAX_LARGE_PAGES}
}

export function pdfSourceKey(source: PdfSource): string {
  return source.kind === 'remote'
    ? `remote:${source.key}`
    : `bytes:${source.key}`
}

async function fetchSource(
  source: PdfSource,
  signal?: AbortSignal,
): Promise<Uint8Array> {
  if (source.kind === 'bytes') {
    // pdf.js transfers the buffer to the worker, which detaches it. Copy so
    // callers can keep using their bytes (eg. the composer still needs them).
    return source.bytes.slice()
  }
  const res = await fetch(source.url, {headers: source.headers, signal})
  if (!res.ok) {
    throw new Error(`Failed to load document: HTTP ${res.status}`)
  }
  return new Uint8Array(await res.arrayBuffer())
}

async function loadDocument(
  source: PdfSource,
  signal?: AbortSignal,
): Promise<pdfjsLib.PDFDocumentProxy> {
  configurePdfjs()
  const data = await fetchSource(source, signal)
  if (signal?.aborted) {
    throw new Error('Aborted')
  }
  const task = pdfjsLib.getDocument({
    data,
    standardFontDataUrl: PDF_STANDARD_FONTS_URL,
  })
  const onAbort = () => task.destroy()
  signal?.addEventListener('abort', onAbort, {once: true})
  try {
    return await task.promise
  } finally {
    signal?.removeEventListener('abort', onAbort)
  }
}

function evictDocuments() {
  if (docs.size <= MAX_LIVE_DOCUMENTS) return
  const evictable = [...docs.entries()]
    .filter(([, entry]) => entry.leases <= 0)
    .sort((a, b) => a[1].lastUsedAt - b[1].lastUsedAt)
  while (docs.size > MAX_LIVE_DOCUMENTS && evictable.length > 0) {
    const [key, entry] = evictable.shift()!
    docs.delete(key)
    entry.doc?.destroy().catch(e => {
      logger.debug('Failed to destroy pdf document', {safeMessage: e})
    })
  }
}

/**
 * Parses a document, reusing an already-parsed copy of the same source. Every
 * successful acquire must be paired with {@link releasePdfDocument}.
 */
export async function acquirePdfDocument(
  source: PdfSource,
  signal?: AbortSignal,
): Promise<pdfjsLib.PDFDocumentProxy> {
  const key = pdfSourceKey(source)
  let entry = docs.get(key)
  if (!entry) {
    const created: DocEntry = {
      promise: undefined as unknown as Promise<pdfjsLib.PDFDocumentProxy>,
      leases: 0,
      lastUsedAt: Date.now(),
    }
    created.promise = loadDocument(source, signal)
      .then(doc => {
        created.doc = doc
        return doc
      })
      .catch(e => {
        // Never cache a failed parse
        if (docs.get(key) === created) {
          docs.delete(key)
        }
        throw e
      })
    entry = created
    docs.set(key, entry)
  }
  entry.leases += 1
  entry.lastUsedAt = Date.now()
  try {
    return await entry.promise
  } catch (e) {
    entry.leases = Math.max(0, entry.leases - 1)
    throw e
  }
}

export function releasePdfDocument(source: PdfSource) {
  const entry = docs.get(pdfSourceKey(source))
  if (!entry) return
  entry.leases = Math.max(0, entry.leases - 1)
  evictDocuments()
}

/** Drops every parsed document. Used on sign-out and in tests. */
export function clearPdfCaches() {
  for (const entry of docs.values()) {
    entry.doc?.destroy().catch(() => {})
  }
  docs.clear()
  for (const page of largePages.values()) releaseCanvas(page)
  for (const page of smallPages.values()) releaseCanvas(page)
  largePages.clear()
  smallPages.clear()
  pendingPages.clear()
}

async function readPageRatio(
  doc: pdfjsLib.PDFDocumentProxy,
  pageNumber: number,
): Promise<number> {
  const page = await doc.getPage(pageNumber)
  const viewport = page.getViewport({scale: 1})
  return viewport.width / viewport.height
}

/** width / height of a page, defaulting to A4 portrait when it can't be read. */
export async function getPdfPageRatio(
  doc: pdfjsLib.PDFDocumentProxy,
  pageNumber = 1,
): Promise<number> {
  try {
    return await readPageRatio(doc, pageNumber)
  } catch (e) {
    logger.warn('Failed to read pdf page ratio', {safeMessage: e})
    return 1 / 1.414
  }
}

function renderPage(
  doc: pdfjsLib.PDFDocumentProxy,
  docKey: string,
  pageNumber: number,
  bucket: number,
  dpr: number,
): Promise<RenderedPdfPage> {
  return doc
    .getPage(pageNumber)
    .then(page => {
      const base = page.getViewport({scale: 1})
      let scale = (bucket * dpr) / base.width
      let viewport = page.getViewport({scale})
      const largest = Math.max(viewport.width, viewport.height)
      if (largest > MAX_CANVAS_DIMENSION) {
        scale = scale * (MAX_CANVAS_DIMENSION / largest)
        viewport = page.getViewport({scale})
      }
      const canvas = document.createElement('canvas')
      canvas.width = Math.max(1, Math.floor(viewport.width))
      canvas.height = Math.max(1, Math.floor(viewport.height))
      const ctx = canvas.getContext('2d', {alpha: false})
      if (!ctx) {
        throw new Error('Could not create a canvas context')
      }
      // A white backing, so pages with no background don't come out transparent
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      const task = page.render({canvasContext: ctx, viewport})
      return task.promise.then(() => ({
        canvas,
        width: bucket,
        height: bucket / (base.width / base.height),
        ratio: base.width / base.height,
      }))
    })
    .catch(e => {
      logger.debug('Failed to render pdf page', {
        docKey,
        pageNumber,
        safeMessage: e,
      })
      throw e
    })
}

/**
 * Renders a page (or returns the cached render of it).
 *
 * `targetWidth` is in CSS pixels; the bucket it lands in is part of the cache
 * key, so resizing by a few pixels doesn't throw away rendered pages.
 */
export async function renderPdfPage({
  doc,
  docKey,
  pageNumber,
  targetWidth,
  dpr = 1,
}: {
  doc: pdfjsLib.PDFDocumentProxy
  docKey: string
  pageNumber: number
  targetWidth: number
  dpr?: number
}): Promise<RenderedPdfPage> {
  const bucket = bucketFor(targetWidth)
  const key = `${docKey}#${pageNumber}@${bucket}`
  const {cache, max} = pickCache(bucket)

  const cached = cache.get(key)
  if (cached) {
    // Touch for LRU ordering
    cache.delete(key)
    cache.set(key, cached)
    return cached
  }

  const pending = pendingPages.get(key)
  if (pending) return pending

  const promise = renderPage(doc, docKey, pageNumber, bucket, dpr)
    .then(rendered => {
      cache.set(key, rendered)
      while (cache.size > max) {
        const oldestKey = cache.keys().next().value
        if (oldestKey === undefined || oldestKey === key) break
        const oldest = cache.get(oldestKey)
        cache.delete(oldestKey)
        if (oldest) releaseCanvas(oldest)
      }
      return rendered
    })
    .finally(() => {
      pendingPages.delete(key)
    })

  pendingPages.set(key, promise)
  return promise
}

/** Warms a page in the cache without caring about the result. */
export function prefetchPdfPage(options: {
  doc: pdfjsLib.PDFDocumentProxy
  docKey: string
  pageNumber: number
  targetWidth: number
  dpr?: number
}) {
  void renderPdfPage(options).catch(() => {})
}

export function supportsOffscreenCanvasRendering() {
  return typeof document !== 'undefined'
}
